/**
 * TagSelector Component
 *
 * Multi-select tag filter component for search refinement.
 * Tags are grouped by category (environment, difficulty, duration, etc.)
 *
 * Features:
 * - Multi-select tag filtering
 * - Tags grouped by category
 * - Experience counts for each tag
 * - Responsive design (desktop dropdown / mobile inline)
 * - WCAG 2.1 AA compliant
 * - Union mode (OR) - experiences matching ANY selected tag
 *
 * Usage:
 * <TagSelector
 *   value={[{ id: 1, name: 'Mountain', slug: 'mountain' }]}
 *   onChange={(tags) => updateFilter('tags', tags)}
 *   variant="dropdown" // or "inline"
 *   onClose={() => setShowDropdown(false)}
 * />
 *
 * Location: apps/web/src/components/search/TagSelector.jsx
 * Created: 2025-11-26
 * Part of: MVP 1.3 Phase 2 - Search Taxonomy & SEO
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTheme } from '../../hooks/useTheme';
import { getAllTags } from '../../services/taxonomyService';

/**
 * TagSelector Component
 * @param {Array} value - Selected tags: [{ id, name, slug, category }, ...]
 * @param {Function} onChange - Callback when selection changes: onChange(tags)
 * @param {string} variant - Display variant: 'dropdown' (desktop) or 'inline' (mobile)
 * @param {Function} onClose - Callback to close dropdown (for dropdown variant)
 */
export default function TagSelector({
  value = [],
  onChange = () => {},
  variant = 'dropdown',
  onClose: _onClose = () => {}
}) {
  const theme = useTheme();

  // State
  const [tags, setTags] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState(new Set());

  // Normalize value to array
  const selectedTags = Array.isArray(value) ? value : [];
  const selectedTagIds = new Set(selectedTags.map(t => t.id));

  // Keep a ref to selectedTagIds so the load callback can access current value without re-running
  const selectedTagIdsRef = useRef(selectedTagIds);
  selectedTagIdsRef.current = selectedTagIds;

  /**
   * Fetch all tags from API
   */
  const loadTags = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getAllTags({ includeCounts: true });
      setTags(data);

      // Auto-expand categories with selected tags
      if (data && data.tagsByCategory) {
        const categoriesToExpand = new Set();
        const currentSelectedIds = selectedTagIdsRef.current;
        Object.entries(data.tagsByCategory).forEach(([category, categoryTags]) => {
          const hasSelectedTag = categoryTags.some(tag => currentSelectedIds.has(tag.id));
          if (hasSelectedTag) {
            categoriesToExpand.add(category);
          }
        });
        setExpandedCategories(categoriesToExpand);
      }
    } catch (err) {
      console.error('[TagSelector] Failed to load tags:', err);
      setError('Failed to load tags. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Load tags on mount
   */
  useEffect(() => {
    loadTags();
  }, [loadTags]);

  /**
   * Toggle category expansion
   */
  const toggleCategory = (category) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(category)) {
      newExpanded.delete(category);
    } else {
      newExpanded.add(category);
    }
    setExpandedCategories(newExpanded);
  };

  /**
   * Handle tag selection toggle
   */
  const handleTagToggle = (tag) => {
    const isSelected = selectedTagIds.has(tag.id);

    if (isSelected) {
      // Deselect
      const updated = selectedTags.filter(t => t.id !== tag.id);
      onChange(updated);
    } else {
      // Select
      const updated = [
        ...selectedTags,
        {
          id: tag.id,
          name: tag.name,
          slug: tag.slug,
          category: tag.category
        }
      ];
      onChange(updated);
    }
  };

  /**
   * Clear all selections
   */
  const handleClear = () => {
    onChange([]);
  };

  /**
   * Render checkbox UI
   */
  const renderCheckbox = (isSelected) => {
    return (
      <div
        className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition ${
          isSelected
            ? `${theme.borderPrimary} ${theme.buttonPrimary.split(' ')[0]}`
            : 'border-gray-300'
        }`}
      >
        {isSelected && (
          <svg
            className="w-3 h-3 text-white"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            strokeWidth={3}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        )}
      </div>
    );
  };

  /**
   * Render experience count badge
   */
  const renderCount = (count) => {
    if (!count) {return null;}
    return (
      <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
        {count}
      </span>
    );
  };

  /**
   * Format category name for display
   */
  const formatCategoryName = (category) => {
    return category
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  /**
   * Render tag item
   */
  const renderTag = (tag) => {
    const isSelected = selectedTagIds.has(tag.id);

    return (
      <button
        key={tag.id}
        onClick={() => handleTagToggle(tag)}
        className={`flex items-center gap-2 p-2 pl-8 rounded transition hover:bg-gray-50 w-full text-left ${
          isSelected ? theme.bgLight : ''
        }`}
        aria-label={`${isSelected ? 'Deselect' : 'Select'} ${tag.name} tag`}
      >
        <span
          className={`text-sm flex-1 ${
            isSelected ? theme.textPrimary + ' font-medium' : 'text-gray-600'
          }`}
        >
          {tag.name}
        </span>
        {renderCount(tag.experience_count)}
        {renderCheckbox(isSelected)}
      </button>
    );
  };

  /**
   * Render tag category group
   */
  const renderCategory = (category, categoryTags) => {
    const isExpanded = expandedCategories.has(category);
    const categoryName = formatCategoryName(category);
    const selectedInCategory = categoryTags.filter(tag => selectedTagIds.has(tag.id)).length;

    return (
      <div key={category} className="border-b border-gray-200 last:border-b-0">
        <button
          onClick={() => toggleCategory(category)}
          className="flex items-center justify-between p-3 w-full hover:bg-gray-50 transition"
          aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${categoryName} tags`}
          aria-expanded={isExpanded}
        >
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-gray-900">
              {categoryName}
            </span>
            {selectedInCategory > 0 && (
              <span className={`text-xs ${theme.textPrimary} font-medium`}>
                ({selectedInCategory})
              </span>
            )}
          </div>
          <svg
            className={`w-4 h-4 text-gray-400 transition-transform ${
              isExpanded ? 'rotate-180' : ''
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {isExpanded && (
          <div className="space-y-1 pb-2">
            {categoryTags.map(tag => renderTag(tag))}
          </div>
        )}
      </div>
    );
  };

  /**
   * Render loading state
   */
  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  /**
   * Render error state
   */
  if (error) {
    return (
      <div className="p-4">
        <div className="text-sm text-red-600 bg-red-50 rounded-lg p-3">
          {error}
        </div>
        <button
          onClick={loadTags}
          className={`mt-3 text-sm ${theme.textPrimary} ${theme.textHover} font-medium`}
        >
          Try Again
        </button>
      </div>
    );
  }

  /**
   * Render empty state
   */
  if (!tags || !tags.tagsByCategory || Object.keys(tags.tagsByCategory).length === 0) {
    return (
      <div className="p-4 text-center text-sm text-gray-500">
        No tags available
      </div>
    );
  }

  /**
   * Render tag groups
   */
  const renderTagGroups = () => {
    return (
      <div className="space-y-0">
        {Object.entries(tags.tagsByCategory)
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([category, categoryTags]) => renderCategory(category, categoryTags))}
      </div>
    );
  };

  // Dropdown variant (desktop)
  if (variant === 'dropdown') {
    return (
      <div className="bg-white rounded-xl shadow-2xl border border-gray-200 min-w-80 max-w-lg">
        <div className="flex items-center justify-between p-4 border-b border-gray-200 sticky top-0 bg-white rounded-t-xl">
          <div>
            <h3 className="font-semibold text-gray-900">Filter by Tags</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Select tags to find matching experiences
            </p>
          </div>
          {selectedTags.length > 0 && (
            <button
              onClick={handleClear}
              className={`text-xs ${theme.textPrimary} ${theme.textHover} font-medium ml-4`}
              aria-label="Clear all tag selections"
            >
              Clear ({selectedTags.length})
            </button>
          )}
        </div>

        <div className="max-h-[28rem] overflow-y-auto">
          {renderTagGroups()}
        </div>

        {selectedTags.length > 0 && (
          <div className="p-3 border-t border-gray-200 bg-gray-50 rounded-b-xl">
            <div className="text-xs text-gray-600">
              Showing experiences with <span className="font-medium">any</span> of these tags
            </div>
          </div>
        )}
      </div>
    );
  }

  // Inline variant (mobile)
  return (
    <div className="bg-white rounded-lg border border-gray-200">
      {selectedTags.length > 0 && (
        <div className="flex items-center justify-between p-3 border-b border-gray-200">
          <span className="text-sm font-medium text-gray-700">
            {selectedTags.length} tag{selectedTags.length !== 1 ? 's' : ''} selected
          </span>
          <button
            onClick={handleClear}
            className={`text-xs ${theme.textPrimary} ${theme.textHover} font-medium`}
            aria-label="Clear all tag selections"
          >
            Clear
          </button>
        </div>
      )}
      <div className="max-h-96 overflow-y-auto">
        {renderTagGroups()}
      </div>
    </div>
  );
}
